import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";

import {
  type FullApiErrorResponse,
  type FullApiResponseWrapper,
  useApiClient,
} from "~/lib/api/axios-client";
import { env } from "~/lib/env";
import {
  keysToCamelCase,
  keysToSnakeCase,
  objectToFormData,
} from "~/utils/caseTransform";

type HttpMethod = "post" | "put" | "patch";

export interface UseApiMutationConfig {
  /**
   * If true, sends the transformed data as FormData instead of JSON
   * @default false
   */
  sendAsFormData?: boolean;
  /**
   * Select which API base URL to use
   * - "default": uses the app's primary API base
   * - "dataExtractor": uses env.VITE_API_URL_DATA_EXTRACTOR
   * @default "default"
   */
  targetApi?: "default" | "dataExtractor" | "insightfulMara";
}

/**
 * Hook for making API mutations with automatic case transformation
 *
 * @template TBody - The type of the request body (in camelCase)
 * @template TResult - The type of the response data
 * @template TContext - The type of context for the mutation
 *
 * @param url - The API endpoint path or absolute URL
 * @param options - React Query mutation options + `sendAsFormData` and `targetApi` flags
 * @param method - HTTP method to use (default: "post")
 *
 * @description
 * Uses the authenticated axios client to perform mutations. Always receives JSON with
 * camelCase keys and automatically transforms them to snake_case for the API request.
 * Response data is automatically transformed back to camelCase. When `options.targetApi`
 * is set to `"dataExtractor"`, the request is sent to the base URL defined by
 * `env.VITE_API_URL_DATA_EXTRACTOR`. Optionally sends transformed data as FormData
 * using the `sendAsFormData` flag.
 *
 * @example
 * // JSON POST request with success callback (default API)
 * const { mutate } = useApiMutation<UserData, ApiResponse>('/users', {
 *   onSuccess: (data) => console.log(data)
 * });
 * mutate({ firstName: 'John', lastName: 'Doe' }); // Sent as { first_name: 'John', last_name: 'Doe' }
 *
 * @example
 * // FormData request
 * const { mutate } = useApiMutation<UserData, ApiResponse>('/upload', {
 *   sendAsFormData: true,
 *   onSuccess: (data) => console.log(data)
 * });
 *
 * @example
 * // Data Extractor API mutation
 * const { mutate } = useApiMutation<JobData, JobResponse>(
 *   '/jobs',
 *   {
 *     targetApi: 'dataExtractor',
 *     onSuccess: (data) => console.log(data)
 *   },
 *   'post'
 * );
 */
export const useApiMutation = <TBody, TResult, TContext = unknown>(
  url: string,
  options?: Omit<
    UseMutationOptions<
      FullApiResponseWrapper<TResult>,
      FullApiErrorResponse,
      TBody,
      TContext
    >,
    "mutationFn"
  > &
    UseApiMutationConfig,
  method: HttpMethod = "post",
): UseMutationResult<
  FullApiResponseWrapper<TResult>,
  FullApiErrorResponse,
  TBody,
  TContext
> => {
  const apiClient = useApiClient();
  const {
    sendAsFormData = false,
    targetApi = "default",
    ...mutationOptions
  } = options || {};

  return useMutation({
    mutationFn: async (variables: TBody) => {
      const transformedData = keysToSnakeCase(variables);

      const requestBody = sendAsFormData
        ? objectToFormData(transformedData)
        : transformedData;

      const base =
        targetApi === "dataExtractor"
          ? env.VITE_API_URL_DATA_EXTRACTOR
          : targetApi === "insightfulMara"
            ? env.VITE_API_URL_INSIGHTFUL_MARA
            : undefined;
      const finalUrl = base ? new URL(url, base).toString() : url;

      const response = await apiClient[method](finalUrl, requestBody);

      const responseData = keysToCamelCase<FullApiResponseWrapper<TResult>>(
        response.data,
      );

      return responseData;
    },
    ...mutationOptions,
  });
};
